import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import { locate } from '../../services/fix';
import { PlaceResult, reverseGeocode, searchPlaces } from '../../services/location';
import { requestRide, RidePoint } from '../../services/rides';
import { colors, hairline, radii, spacing } from '../../theme';

/**
 * Ordering a driver.
 *
 * ---------------------------------------------------------------------------
 * NO PRICE, ANYWHERE, ON PURPOSE
 * ---------------------------------------------------------------------------
 * Ride pricing does not exist server-side - there is no fare, no estimate and
 * no charge. So this sheet shows no money at all rather than a number that
 * would be invented here and contradicted the moment pricing lands. The
 * customer books a driver and is told what happens next; what it costs is a
 * conversation the product has not had yet.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PICKUP DEFAULTS TO THE PHONE AND THE DROP DOES NOT
 * ---------------------------------------------------------------------------
 * Somebody ordering a driver is standing where they want collecting from - so
 * the pickup is prefilled from the device and is *editable*, not fixed. The
 * drop cannot be guessed at all, so it is the one field this sheet insists on.
 *
 * `locate()` is reused rather than `LocationScreen`'s two-stage fix. Its
 * budget is tuned for the SOS button - a cached position is welcome and the
 * deadline is hard - which is a worse fit for a pin than for an alarm, but the
 * right trade here: the customer can see the address it resolved to and search
 * for a different one, and a sheet that spends twelve seconds locking GPS
 * before it will draw is a sheet people back out of.
 */

/** How far ahead "Later" can schedule, as relative presets. */
const LATER_PRESETS: { label: string; minutes: number }[] = [
  { label: 'In 30 min', minutes: 30 },
  { label: 'In 1 hour', minutes: 60 },
  { label: 'In 2 hours', minutes: 120 },
];

/** Debounce on the drop search, so a keystroke does not become a request. */
const SEARCH_DEBOUNCE_MS = 350;

type Point = RidePoint & { label: string };

type Field = 'pickup' | 'drop';

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Handed the queued ride so the caller can show "finding you a driver". */
  onBooked: (rideId: string) => void;
};

function RideBookingSheet({ visible, onClose, onBooked }: Props) {
  const [pickup, setPickup] = useState<Point | null>(null);
  const [drop, setDrop] = useState<Point | null>(null);

  /** Which field the search list is currently filling. Null = neither. */
  const [editing, setEditing] = useState<Field | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlaceResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [locating, setLocating] = useState(false);
  const [whenMinutes, setWhenMinutes] = useState<number | null>(null); // null = now
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Aborts the in-flight search when another keystroke lands or the sheet
   * closes. Without it a slow response for "ind" can arrive after the one for
   * "indore" and overwrite the better list with the worse one.
   */
  const searchAbort = useRef<AbortController | null>(null);

  // --- The pickup, from the device ---------------------------------------

  const fillPickupFromDevice = useCallback(async () => {
    setLocating(true);
    setError(null);
    try {
      const fix = await locate();
      if (!fix) {
        setError(
          'Could not get your location. Search for your pickup point instead.',
        );
        return;
      }

      // The coordinates are what the driver is routed to; the label is only
      // what the customer reads. So a failed reverse geocode downgrades the
      // label and never the booking.
      let label = 'Current location';
      try {
        const place = await reverseGeocode(fix.latitude, fix.longitude);
        label = place.label || label;
      } catch {
        // Deliberately swallowed - see above.
      }

      setPickup({ lat: fix.latitude, lng: fix.longitude, addressText: label, label });
    } finally {
      setLocating(false);
    }
  }, []);

  /* Prefill once per opening, and only the pickup. */
  useEffect(() => {
    if (!visible) return;
    if (pickup) return;
    void fillPickupFromDevice();
  }, [visible, pickup, fillPickupFromDevice]);

  /* Reset when the sheet closes, so the next opening is not last trip's. */
  useEffect(() => {
    if (visible) return;
    searchAbort.current?.abort();
    setEditing(null);
    setQuery('');
    setResults([]);
    setError(null);
    setWhenMinutes(null);
    setPickup(null);
    setDrop(null);
  }, [visible]);

  // --- Searching ----------------------------------------------------------

  useEffect(() => {
    if (!editing) return;
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      searchAbort.current?.abort();
      const controller = new AbortController();
      searchAbort.current = controller;

      setSearching(true);
      searchPlaces(term, controller.signal)
        .then(setResults)
        .catch(() => {
          // An aborted search is the normal case, not a failure worth showing.
          if (!controller.signal.aborted) {
            setError('Could not search right now. Check your connection.');
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, editing]);

  const choose = (place: PlaceResult) => {
    const point: Point = {
      lat: place.lat,
      lng: place.lng,
      addressText: [place.title, place.subtitle].filter(Boolean).join(', '),
      label: place.title,
    };
    if (editing === 'pickup') setPickup(point);
    else setDrop(point);

    setEditing(null);
    setQuery('');
    setResults([]);
  };

  // --- Booking ------------------------------------------------------------

  const confirm = async () => {
    if (!pickup || !drop || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const ride = await requestRide({
        pickup,
        drop,
        scheduledPickupAt: whenMinutes
          ? new Date(Date.now() + whenMinutes * 60_000)
          : undefined,
      });
      onBooked(ride.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not book your driver. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --- Rendering ----------------------------------------------------------

  const renderField = (field: Field, value: Point | null, placeholder: string) => (
    <Pressable
      style={styles.field}
      onPress={() => {
        setEditing(field);
        setQuery('');
        setResults([]);
      }}
    >
      <View
        style={[
          styles.dot,
          field === 'pickup' ? styles.dotPickup : styles.dotDrop,
        ]}
      />
      <View style={styles.fieldText}>
        <Text variant="caption" color={colors.inkMuted}>
          {field === 'pickup' ? 'Pickup' : 'Drop'}
        </Text>
        <Text
          variant="body"
          color={value ? colors.ink : colors.inkMuted}
          numberOfLines={1}
        >
          {value?.label ?? placeholder}
        </Text>
      </View>
      {field === 'pickup' && locating ? (
        <ActivityIndicator size="small" color={colors.inkMuted} />
      ) : (
        <Icon name="chevronRight" size={16} color={colors.inkMuted} />
      )}
    </Pressable>
  );

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      heightRatio={editing ? 0.85 : undefined}
      footer={
        editing ? undefined : (
          <Button
            label={submitting ? 'Booking…' : 'Confirm'}
            size="lg"
            onPress={() => void confirm()}
            disabled={!pickup || !drop || submitting}
          />
        )
      }
    >
      {editing ? (
        <View style={styles.searchPane}>
          <View style={styles.searchRow}>
            <Icon name="search" size={18} color={colors.inkMuted} />
            <TextInput
              autoFocus
              value={query}
              onChangeText={setQuery}
              placeholder={
                editing === 'pickup' ? 'Search pickup point' : 'Where to?'
              }
              placeholderTextColor={colors.inkMuted}
              style={styles.searchInput}
              returnKeyType="search"
            />
            <Pressable onPress={() => setEditing(null)} hitSlop={8}>
              <Text variant="body" color={colors.primary}>
                Cancel
              </Text>
            </Pressable>
          </View>

          {editing === 'pickup' ? (
            <Pressable style={styles.myLocation} onPress={() => {
              setEditing(null);
              void fillPickupFromDevice();
            }}>
              <Icon name="pin" size={16} color={colors.primary} />
              <Text variant="body" color={colors.primary}>
                Use my current location
              </Text>
            </Pressable>
          ) : null}

          <ScrollView keyboardShouldPersistTaps="handled">
            {searching ? (
              <ActivityIndicator style={styles.spinner} color={colors.inkMuted} />
            ) : null}
            {results.map(place => (
              <Pressable
                key={place.id}
                style={styles.result}
                onPress={() => choose(place)}
              >
                <Icon name="pin" size={16} color={colors.inkMuted} />
                <View style={styles.resultText}>
                  <Text variant="body" color={colors.ink} numberOfLines={1}>
                    {place.title}
                  </Text>
                  {place.subtitle ? (
                    <Text
                      variant="caption"
                      color={colors.inkMuted}
                      numberOfLines={1}
                    >
                      {place.subtitle}
                    </Text>
                  ) : null}
                </View>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      ) : (
        <View style={styles.form}>
          <Text variant="title" color={colors.ink}>
            Book a driver
          </Text>

          <View style={styles.fields}>
            {renderField('pickup', pickup, 'Getting your location…')}
            <View style={styles.divider} />
            {renderField('drop', drop, 'Where to?')}
          </View>

          <Text variant="caption" color={colors.inkMuted}>
            When
          </Text>
          <View style={styles.when}>
            <Pressable
              style={[styles.chip, whenMinutes === null && styles.chipOn]}
              onPress={() => setWhenMinutes(null)}
            >
              <Text
                variant="caption"
                color={whenMinutes === null ? colors.onPrimary : colors.ink}
              >
                Now
              </Text>
            </Pressable>
            {LATER_PRESETS.map(preset => (
              <Pressable
                key={preset.minutes}
                style={[
                  styles.chip,
                  whenMinutes === preset.minutes && styles.chipOn,
                ]}
                onPress={() => setWhenMinutes(preset.minutes)}
              >
                <Text
                  variant="caption"
                  color={
                    whenMinutes === preset.minutes ? colors.onPrimary : colors.ink
                  }
                >
                  {preset.label}
                </Text>
              </Pressable>
            ))}
          </View>

          {error ? (
            <Text variant="caption" color={colors.danger}>
              {error}
            </Text>
          ) : null}
        </View>
      )}
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  form: { gap: spacing.md, paddingBottom: spacing.sm },
  fields: {
    ...hairline,
    borderRadius: radii.md,
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
  },
  fieldText: { flex: 1, gap: 2 },
  divider: {
    height: hairline.borderWidth,
    backgroundColor: hairline.borderColor,
    marginLeft: spacing.md,
  },
  dot: { width: 10, height: 10, borderRadius: 5 },
  dotPickup: { backgroundColor: colors.primary },
  dotDrop: { backgroundColor: colors.danger },
  when: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.pill,
    ...hairline,
  },
  chipOn: { backgroundColor: colors.primary, borderColor: colors.primary },
  searchPane: { flex: 1, gap: spacing.sm },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    ...hairline,
    borderRadius: radii.md,
    paddingHorizontal: spacing.md,
  },
  searchInput: { flex: 1, paddingVertical: spacing.sm, color: colors.ink },
  myLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  spinner: { marginTop: spacing.md },
  result: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  resultText: { flex: 1, gap: 2 },
});

export default RideBookingSheet;
